import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {Alert} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {AxiosError} from 'axios';
import {translations} from '../../i18n/translations';
import {ExternalAccountType} from '../../backend/enums/external-account-type';
import {ExternalAccountsRepository} from '../../repositories/external-accounts';
import {getExternalAccountTypeLabel} from '../../utils/functions/get-external-account-type-label';
import {ContentPage} from '../content-page/content-page';
import {PageHeader} from '../page-header/page-header';
import {SelectInput} from '../select-input/select-input';
import {AlertModal} from '../alert-modal/alert-modal';
import {LoadingContent} from '../loading-content/loading-content';
import {SubmitButton, SubmitButtonState} from '../submit-button/submit-button';
import {useSnackBar} from '../../contexts/snack-bar/context';

/**
 * Form to create a new external account for a user.
 *
 * There's no update path, if an account of the chosen type already exists,
 * the user must delete it first and then create a new one.
 *
 * Only system administrators can access this form.
 */
export const AssociateExternalAccountForm = (props: {userId: string; redirectUrl: string}) => {
  const {t} = useTranslation();
  const snackBarContext = useSnackBar();
  let history = useHistory();

  const [existingTypes, setExistingTypes] = useState<Array<ExternalAccountType> | undefined>(
    undefined
  );
  const [selectedType, setSelectedType] = useState<ExternalAccountType | undefined>(undefined);
  const [submitButtonState, setSubmitButtonState] = useState<SubmitButtonState>(
    SubmitButtonState.Normal
  );
  const [submitDialogOpened, setSubmitDialogOpened] = useState<boolean>(false);

  // On mount, get the existing account types so we can filter them out of the dropdown
  useEffect(() => {
    const getExistingTypes = async () => {
      try {
        const accounts = await ExternalAccountsRepository.instance.getForUser(props.userId);
        setExistingTypes(accounts.map((obj) => obj.type));
      } catch (error) {
        console.debug('🔴 Could not get the existing external accounts.', error);
        setExistingTypes([]);
      }
    };

    getExistingTypes();
  }, [props.userId]);

  const availableTypes = Object.values(ExternalAccountType).filter(
    (type) => !existingTypes?.includes(type)
  );

  // Save
  const handleSaveChanges = (event: any) => {
    event.preventDefault();
    setSubmitDialogOpened(true);
  };

  // Submit
  const handleSubmit = async () => {
    try {
      setSubmitDialogOpened(false);
      setSubmitButtonState(SubmitButtonState.Loading);

      if (!selectedType) {
        throw new Error('Empty external account type.');
      }

      await ExternalAccountsRepository.instance.create(props.userId, selectedType);

      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.external_accounts.associate.success_message, {
          type: getExternalAccountTypeLabel(selectedType),
        }),
      });

      // Wait some time just to make sure the button state was updated
      setTimeout(() => {
        history.push(props.redirectUrl);
      }, 500);
    } catch (error) {
      console.debug('🔴 Could not create the external account.', error);

      setSubmitButtonState(SubmitButtonState.Error);

      let message: string = t(translations.external_accounts.associate.errors.generic_message);

      if ((error as AxiosError).response?.status === 409) {
        message = t(translations.external_accounts.associate.errors.conflict_message);
      } else if ((error as AxiosError).response?.status === 502) {
        message = t(translations.external_accounts.associate.errors.provider_error_message);
      }

      snackBarContext.setOfficeToastNotification({severity: 'error', message});
    }
  };

  return (
    <ContentPage>
      {!existingTypes && <LoadingContent marginTop="32px" />}
      {existingTypes && availableTypes.length === 0 && (
        <Alert severity="info" className="associate-external-account-form-info-message">
          {t(translations.external_accounts.associate.no_available_types_message)}
        </Alert>
      )}
      {existingTypes && availableTypes.length > 0 && (
        <>
          <form onSubmit={handleSaveChanges}>
            <PageHeader
              title={t(translations.external_accounts.associate.title)}
              isSmallTitle={true}
              rightButtons={
                <SubmitButton
                  disabled={!selectedType}
                  state={submitButtonState}
                  onStateChange={setSubmitButtonState}
                  normalStateLabel={t(translations.external_accounts.associate.save_button)}
                  loadingStateLabel={t(translations.external_accounts.associate.saving_button)}
                  errorStateLabel={t(
                    translations.external_accounts.associate.errors.error_on_save_button
                  )}
                  endIcon={<SaveIcon />}
                  type="submit"
                />
              }
            />
            <SelectInput
              label={t(translations.external_accounts.associate.type_input)}
              items={availableTypes.map((type) => {
                return {value: type, label: getExternalAccountTypeLabel(type)};
              })}
              value={selectedType}
              handleChange={(value) => setSelectedType(value as ExternalAccountType)}
              disabled={submitButtonState === SubmitButtonState.Loading}
              required={true}
            />
          </form>
          <AlertModal
            id="confirm-associate-external-account"
            opened={submitDialogOpened}
            title={t(translations.external_accounts.associate.save_dialog_title, {
              type: selectedType ? getExternalAccountTypeLabel(selectedType) : '',
            })}
            content={t(translations.external_accounts.associate.save_dialog_message)}
            handleClose={() => setSubmitDialogOpened(false)}
            actions={[
              {
                label: t(translations.external_accounts.associate.save_dialog_action_cancel_button),
                type: 'neutral',
                handleClick: () => setSubmitDialogOpened(false),
              },
              {
                label: t(translations.external_accounts.associate.save_dialog_action_save_button),
                type: 'nondestructive',
                handleClick: handleSubmit,
              },
            ]}
          />
        </>
      )}
    </ContentPage>
  );
};
